import { useRef, useState } from "react";

/**
 * Tilt3D — a subtle 3D perspective tilt on mouse hover.
 * Uses transform: rotateX/rotateY based on cursor position.
 * Corporate-friendly: default max tilt is small (6deg) and disabled on touch.
 */
export default function Tilt3D({
  children,
  className = "",
  max = 6,
  scale = 1.01,
  glare = true,
  ...rest
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({});
  const [glarePos, setGlarePos] = useState({ x: 50, y: 50, opacity: 0 });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const rx = (0.5 - py) * (max * 2);
    const ry = (px - 0.5) * (max * 2);
    setStyle({
      transform: `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${scale})`,
      transition: "transform 120ms ease-out",
    });
    setGlarePos({ x: px * 100, y: py * 100, opacity: 0.35 });
  };

  const reset = () => {
    setStyle({
      transform: "perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)",
      transition: "transform 400ms cubic-bezier(0.22, 1, 0.36, 1)",
    });
    setGlarePos((g) => ({ ...g, opacity: 0 }));
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      onMouseEnter={onMove}
      className={`relative ${className}`}
      style={{ transformStyle: "preserve-3d", ...style }}
      {...rest}
    >
      {children}
      {glare && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 transition-opacity duration-300"
          style={{
            opacity: glarePos.opacity,
            background: `radial-gradient(circle at ${glarePos.x}% ${glarePos.y}%, rgba(255,255,255,0.55), rgba(255,255,255,0) 55%)`,
            mixBlendMode: "overlay",
          }}
        />
      )}
    </div>
  );
}
